
import { Business } from '../types';

export interface BusinessStatus {
  isOpen: boolean;
  message: string;
  nextEvent?: {
    type: 'opens' | 'closes';
    time: string;
    day?: string;
  };
}

export function isBusinessOpen(business: Business): BusinessStatus {
  if (!business.opening_hours || !Array.isArray(business.opening_hours) || business.opening_hours.length === 0) {
    return { isOpen: false, message: 'Horário não informado' };
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 (Domingo) to 6 (Sábado)
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const daysMap: Record<number, string> = {
    0: 'domingo', 1: 'segunda', 2: 'terça', 3: 'quarta', 4: 'quinta', 5: 'sexta', 6: 'sábado'
  };

  const dayIndices: Record<string, number> = {
    'domingo': 0, 'segunda': 1, 'terca': 2, 'quarta': 3, 'quinta': 4, 'sexta': 5, 'sabado': 6,
    'dom': 0, 'seg': 1, 'ter': 2, 'qua': 3, 'qui': 4, 'sex': 5, 'sab': 6, 'sábado': 6, 'terça': 2
  };

  const normalize = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const currentDayName = normalize(daysMap[currentDay]);
  
  let todayHoursMatch: string | null = null;

  for (const hour of business.opening_hours) {
    let hourStr = "";
    if (typeof hour === 'string') {
      hourStr = hour;
    } else if (hour && typeof hour === 'object' && (hour as any).day && (hour as any).hours) {
      hourStr = `${(hour as any).day}: ${(hour as any).hours}`;
    } else if (hour && typeof hour === 'object' && (hour as any).label && (hour as any).value) {
      hourStr = `${(hour as any).label}: ${(hour as any).value}`;
    } else {
      continue;
    }

    const normalizedHour = normalize(hourStr);
    const parts = normalizedHour.split(/[:\-–—\.]/);
    const dayPart = parts[0]?.trim() || "";

    // Exact match for the day
    if (dayPart === currentDayName || dayPart.includes(currentDayName)) {
      todayHoursMatch = hourStr;
      break;
    }

    // Check for ranges like "segunda a sexta" or "seg-sex"
    if (dayPart.includes(' a ') || dayPart.includes('-') || dayPart.includes(' at ') || dayPart.includes(' to ')) {
      const rangeParts = dayPart.split(/(\s+a\s+|\s+at\s+|\s+to\s+|-|–|—)/);
      if (rangeParts.length >= 3) {
        const startDay = normalize(rangeParts[0]);
        const endDay = normalize(rangeParts[rangeParts.length - 1]);
        
        const startIdx = dayIndices[startDay];
        const endIdx = dayIndices[endDay];

        if (startIdx !== undefined && endIdx !== undefined) {
          // Normal range (e.g. Mon-Fri)
          if (startIdx <= endIdx) {
            if (currentDay >= startIdx && currentDay <= endIdx) {
              todayHoursMatch = hourStr;
              break;
            }
          } else {
            // Wrapping range (e.g. Sat-Mon)
            if (currentDay >= startIdx || currentDay <= endIdx) {
              todayHoursMatch = hourStr;
              break;
            }
          }
        }
      }
    }
  }

  if (!todayHoursMatch) {
    return { isOpen: false, message: 'Fechado hoje' };
  }

  const timePart = todayHoursMatch.includes(':') 
    ? todayHoursMatch.split(':').slice(1).join(':').trim()
    : todayHoursMatch.replace(/^[^\d]*/, '').trim();

  if (timePart.toLowerCase().includes('fechado')) {
    return { isOpen: false, message: 'Fechado' };
  }

  if (timePart.toLowerCase().includes('24h')) {
    return { isOpen: true, message: 'Aberto agora (24h)' };
  }

  const shifts = timePart.split(/[,;|]/).map(s => s.trim());
  let isOpen = false;
  
  for (const shift of shifts) {
    const times = shift.split(/(\s+às\s+|\s+as\s+|\s+at\s+|\s+to\s+|\s+a\s+|-|–|—)/i)
      .map(t => t.trim())
      .filter(t => !/^(às|as|at|to|a|-|–|—)$/i.test(t));
    
    if (times.length >= 2) {
      const parseTime = (t: string) => {
        const [h, m] = t.split(/[:h]/i).map(Number);
        return h * 60 + (isNaN(m) ? 0 : m);
      };

      const startTotal = parseTime(times[0]);
      const endTotal = parseTime(times[1]);

      if (isNaN(startTotal) || isNaN(endTotal)) continue;

      if (endTotal < startTotal) {
        // Overnight
        if (currentMinutes >= startTotal || currentMinutes < endTotal) {
          isOpen = true;
          break;
        }
      } else {
        if (currentMinutes >= startTotal && currentMinutes < endTotal) {
          isOpen = true;
          break;
        }
      }
    }
  }

  return { 
    isOpen, 
    message: isOpen ? 'Aberto agora' : 'Fechado agora' 
  };
}

export function formatPhoneNumber(phone: string | undefined | null): string {
  if (!phone) return 'Não informado';
  let digits = phone.replace(/\D/g, '');
  if (!digits) return phone;

  // Strip country code if it starts with 55 AND contains a DDD + number
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    digits = digits.substring(2);
  }

  if (digits.length === 11) {
    // DDD + 9 digits: (XX) 9XXXX.XXXX
    const ddd = digits.substring(0, 2);
    const first = digits.substring(2, 7);
    const second = digits.substring(7);
    return `(${ddd}) ${first}.${second}`;
  } else if (digits.length === 10) {
    // DDD + 8 digits: (XX) XXXX.XXXX
    const ddd = digits.substring(0, 2);
    const first = digits.substring(2, 6);
    const second = digits.substring(6);
    return `(${ddd}) ${first}.${second}`;
  } else if (digits.length === 9) {
    // 9 digits: 9XXXX.XXXX
    const first = digits.substring(0, 5);
    const second = digits.substring(5);
    return `${first}.${second}`;
  } else if (digits.length === 8) {
    // 8 digits: XXXX.XXXX
    const first = digits.substring(0, 4);
    const second = digits.substring(4);
    return `${first}.${second}`;
  }

  return phone;
}

/**
 * Normaliza qualquer texto removendo acentos, caracteres especiais,
 * convertendo para minúsculas e eliminando espaços extras e pontuações.
 * Exemplo: "Gráfica" -> "grafica", "FARMÁCIA " -> "farmacia", "Construção" -> "construcao"
 */
export function normalizeSearchString(str: any): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacríticos (á -> a, ç -> c, õ -> o)
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ') // substitui pontuações por espaço
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calcula a distância Levenshtein entre duas strings para tolerar erros de digitação (typos)
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Gera raízes morfológicas e variações de singular/plural em português.
 * Trata casos como:
 * - gráficas / gráfica / gráfico / graficos -> "grafic", "grafica", "graficas"
 * - farmácias / farmácia -> "farmac", "farmacia", "farmacias"
 * - restaurantes / restaurante -> "restauran", "restaurante", "restaurantes"
 * - hotéis / hotel -> "hotel", "hoteis"
 * - móveis / móvel -> "movel", "moveis"
 * - construções / construção -> "construcao", "construcoes", "construc"
 * - pães / pão -> "pao", "paes"
 * - jardins / jardim -> "jardim", "jardins"
 */
export function getPortugueseWordVariants(word: string): string[] {
  const norm = normalizeSearchString(word);
  if (!norm) return [];
  if (norm.length <= 2) return [norm];

  const variants = new Set<string>();
  variants.add(norm);

  // 1. Plural simples terminando em 's'
  if (norm.endsWith('s') && norm.length >= 4) {
    variants.add(norm.slice(0, -1)); // graficas -> grafica
  } else if (!norm.endsWith('s') && norm.length >= 3) {
    variants.add(norm + 's'); // grafica -> graficas
  }

  // 2. Plurais em 'es' (bares <-> bar, flores <-> flor)
  if (norm.endsWith('es') && norm.length >= 4) {
    variants.add(norm.slice(0, -2)); // bares -> bar
    variants.add(norm.slice(0, -1)); // restaurantes -> restaurante
  } else if (norm.endsWith('r') || norm.endsWith('z')) {
    variants.add(norm + 'es'); // bar -> bares
  }

  // 3. Plurais em 'is' (hoteis <-> hotel, moveis <-> movel, papeis <-> papel, animais <-> animal)
  if (norm.endsWith('eis') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'el'); // hoteis -> hotel, moveis -> movel
  } else if (norm.endsWith('el') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'eis'); // hotel -> hoteis
  }

  if (norm.endsWith('ais') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'al'); // animais -> animal
  } else if (norm.endsWith('al') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'ais'); // animal -> animais
  }

  if (norm.endsWith('ois') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'ol'); // farois -> farol
  } else if (norm.endsWith('ol') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'ois'); // farol -> farois
  }

  if (norm.endsWith('uis') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'ul'); // azuis -> azul
  } else if (norm.endsWith('ul') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'uis'); // azul -> azuis
  }

  // 4. Plurais em 'oes' / 'aes' / 'aos' (construcoes <-> construcao, paes <-> pao)
  if (norm.endsWith('oes') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'ao'); // construcoes -> construcao
  } else if (norm.endsWith('aes') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'ao'); // paes -> pao
  } else if (norm.endsWith('aos') && norm.length >= 4) {
    variants.add(norm.slice(0, -3) + 'ao'); // irmaos -> irmao
  } else if (norm.endsWith('ao') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'oes');
    variants.add(norm.slice(0, -2) + 'aes');
    variants.add(norm.slice(0, -2) + 'aos');
  }

  // 5. Plurais em 'ns' (jardins <-> jardim, viagens <-> viagem)
  if (norm.endsWith('ns') && norm.length >= 3) {
    variants.add(norm.slice(0, -2) + 'm'); // jardins -> jardim
  } else if (norm.endsWith('m') && norm.length >= 3) {
    variants.add(norm.slice(0, -1) + 'ns'); // jardim -> jardins
  }

  // 6. Stemming de sufixos comuns em categorias comerciais
  // Suffixes: icas, ica, icos, ico, arias, aria, erias, eria, orias, oria, istas, ista, eiros, eiro, eiras, eira
  const suffixRules = [
    { regex: /(icas|ica|icos|ico)$/, stem: 'ic' },
    { regex: /(arias|aria|erias|eria|orias|oria)$/, stem: '' },
    { regex: /(istas|ista)$/, stem: 'ist' },
    { regex: /(eiros|eiro|eiras|eira)$/, stem: 'eir' },
    { regex: /(acoes|acao)$/, stem: 'ac' },
    { regex: /(encias|encia|ancias|ancia)$/, stem: 'enc' },
    { regex: /(idades|idade)$/, stem: 'idad' },
    { regex: /(mentos|mento)$/, stem: 'ment' },
  ];

  for (const rule of suffixRules) {
    if (rule.regex.test(norm) && norm.length >= 5) {
      const base = norm.replace(rule.regex, rule.stem);
      if (base.length >= 3) {
        variants.add(base);
      }
    }
  }

  // Raiz básica removendo vogal temática final (a, o, e)
  if (norm.length >= 5) {
    const rawRoot = norm.replace(/[asoe]+$/, '');
    if (rawRoot.length >= 4) {
      variants.add(rawRoot);
    }
  }

  return Array.from(variants);
}

/**
 * Compara se duas palavras ou tokens são equivalentes linguisticamente em português
 * (mesma raiz, variação singular/plural, substring ou tolerância a erro de digitação).
 */
export function areTokensEquivalent(tokenA: string, tokenB: string): boolean {
  if (!tokenA || !tokenB) return false;
  const aNorm = normalizeSearchString(tokenA);
  const bNorm = normalizeSearchString(tokenB);
  
  if (aNorm === bNorm) return true;
  if (!aNorm || !bNorm) return false;

  // Substring direta se pelo menos 3 caracteres
  if (aNorm.length >= 3 && bNorm.length >= 3) {
    if (aNorm.includes(bNorm) || bNorm.includes(aNorm)) return true;
  }

  // Variações e Stems morfológicos
  const variantsA = getPortugueseWordVariants(aNorm);
  const variantsB = getPortugueseWordVariants(bNorm);

  for (const va of variantsA) {
    for (const vb of variantsB) {
      if (va === vb) return true;
      if (va.length >= 4 && vb.length >= 4) {
        if (va.includes(vb) || vb.includes(va)) return true;
      }
    }
  }

  // Tolerância a pequenos erros de digitação (Levenshtein)
  const minLen = Math.min(aNorm.length, bNorm.length);
  if (minLen >= 4) {
    const maxDistance = minLen >= 7 ? 2 : 1;
    if (levenshteinDistance(aNorm, bNorm) <= maxDistance) {
      return true;
    }
  }

  return false;
}

/**
 * Extrai todas as categorias de uma empresa em formato de array de strings limpas,
 * suportando os campos `category`, `categories` (array ou JSON string) e múltiplos valores separados por vírgula.
 */
export function extractBusinessCategories(business: any): string[] {
  if (!business) return [];
  const list: string[] = [];

  if (business.category && typeof business.category === 'string') {
    const parts = business.category.split(/[,;/|]+/).map((s: string) => s.trim()).filter(Boolean);
    list.push(...parts);
  }

  if (Array.isArray(business.categories)) {
    for (const c of business.categories) {
      if (typeof c === 'string' && c.trim()) {
        const parts = c.split(/[,;/|]+/).map((s: string) => s.trim()).filter(Boolean);
        list.push(...parts);
      }
    }
  } else if (typeof business.categories === 'string' && business.categories.trim()) {
    try {
      const parsed = JSON.parse(business.categories);
      if (Array.isArray(parsed)) {
        list.push(...parsed.filter((s: any) => typeof s === 'string' && s.trim()));
      }
    } catch {
      const parts = business.categories.split(/[,;/|]+/).map((s: string) => s.trim()).filter(Boolean);
      list.push(...parts);
    }
  }

  return Array.from(new Set(list));
}

/**
 * Verifica se o nome ou slug de uma categoria corresponde a uma busca de categoria
 * (ex: "Gráficas" corresponde a "Gráfica", "Farmácia" a "Farmácias", etc.)
 */
export function matchesCategoryName(categoryName: string, queryCategory: string): boolean {
  if (!categoryName || !queryCategory) return false;
  if (categoryName === queryCategory) return true;
  
  const normCat = normalizeSearchString(categoryName);
  const normQuery = normalizeSearchString(queryCategory);
  
  if (!normCat || !normQuery) return false;
  if (normCat === 'todas' || normQuery === 'todas' || normCat === 'all' || normQuery === 'all') return true;
  if (normCat === normQuery) return true;

  return areTokensEquivalent(normCat, normQuery);
}

/**
 * Verifica se uma empresa pertence a uma categoria específica de forma flexível e robusta,
 * tolerando variações de acentuação, maiúsculas/minúsculas, plurais/singulares e múltiplos cadastros.
 */
export function matchesBusinessCategory(
  business: any,
  activeCategory?: string | null,
  guideCategories?: any[]
): boolean {
  if (!activeCategory || activeCategory === 'Todas' || activeCategory === 'todas' || activeCategory === 'all') {
    return true;
  }

  const targetNorm = normalizeSearchString(activeCategory);
  if (!targetNorm || targetNorm === 'todas' || targetNorm === 'all') return true;

  const bCategories = extractBusinessCategories(business);
  
  // 1. Verificação direta nas categorias da empresa (com e sem acento, singular e plural)
  for (const cat of bCategories) {
    if (matchesCategoryName(cat, activeCategory)) {
      return true;
    }
  }

  // 2. Verificação pelo catálogo de categorias cadastradas (guideCategories)
  if (Array.isArray(guideCategories) && guideCategories.length > 0) {
    const matchedGuideCat = guideCategories.find(gc => {
      return matchesCategoryName(gc.name, activeCategory) || 
             matchesCategoryName(gc.slug, activeCategory) || 
             String(gc.id) === targetNorm;
    });

    if (matchedGuideCat) {
      const guideId = String(matchedGuideCat.id);

      for (const cat of bCategories) {
        if (matchesCategoryName(cat, matchedGuideCat.name) || matchesCategoryName(cat, matchedGuideCat.slug)) {
          return true;
        }
      }

      if (business.category_id && String(business.category_id) === guideId) return true;
      if (business.guide_category_id && String(business.guide_category_id) === guideId) return true;
    }
  }

  // 3. Verificação por palavras-chave (keywords) com equivalência morfológica
  if (business.keywords) {
    const kwTokens = normalizeSearchString(business.keywords).split(/\s+/).filter(Boolean);
    const targetTokens = targetNorm.split(/\s+/).filter(Boolean);
    const kwMatches = targetTokens.some(tTok => kwTokens.some(kTok => areTokensEquivalent(kTok, tTok)));
    if (kwMatches) return true;
  }

  // 4. Verificação no slug ou nome se a categoria estiver contida no nome/slug da empresa
  const nameTokens = normalizeSearchString(business.name).split(/\s+/).filter(Boolean);
  const slugTokens = normalizeSearchString(business.slug?.replace(/[-_]/g, ' ')).split(/\s+/).filter(Boolean);
  const targetTokens = targetNorm.split(/\s+/).filter(Boolean);
  
  const matchesNameOrSlug = targetTokens.some(tTok => 
    nameTokens.some(nTok => areTokensEquivalent(nTok, tTok)) ||
    slugTokens.some(sTok => areTokensEquivalent(sTok, tTok))
  );

  if (matchesNameOrSlug) return true;

  return false;
}

/**
 * Validação se a empresa está ativa e publicada no guia
 */
export function isBusinessActive(business: any): boolean {
  if (!business) return false;
  const isPublished = business.status === 'published' || !business.status;
  const isNotExpired = !business.expires_at || new Date(business.expires_at) > new Date();
  const isNotExplicitlyExpired = business.payment_status !== 'expired';
  return isPublished && isNotExpired && isNotExplicitlyExpired;
}

/**
 * Motor de busca completo e ultra-inteligente para o Guia Comercial,
 * compatível com o comportamento de grandes buscadores (Google, etc.).
 * Cruzando termos de busca ("o que procura"), localização ("onde procura") e categoria selecionada,
 * com suporte nativo a acentuação, plurais, singulares, sufixos, tolerância a digitação e múltiplos tokens.
 */
export function matchesBusinessSearch(
  business: any,
  searchWhat?: string | null,
  searchWhere?: string | null,
  activeCategory?: string | null,
  guideCategories?: any[]
): boolean {
  if (!business) return false;

  // 1. Filtragem por categoria ativa (se selecionada)
  if (activeCategory && !matchesBusinessCategory(business, activeCategory, guideCategories)) {
    return false;
  }

  // 2. Filtragem por localização ("Onde procura")
  if (searchWhere && searchWhere.trim()) {
    const whereTokens = normalizeSearchString(searchWhere).split(/\s+/).filter(Boolean);
    const cityTokens = normalizeSearchString(business.city).split(/\s+/).filter(Boolean);
    const addressTokens = normalizeSearchString(business.address).split(/\s+/).filter(Boolean);
    const kwTokens = normalizeSearchString(business.keywords).split(/\s+/).filter(Boolean);
    const allWhereTargets = [...cityTokens, ...addressTokens, ...kwTokens];

    const whereMatches = whereTokens.every(wTok => 
      allWhereTargets.some(targetTok => areTokensEquivalent(targetTok, wTok))
    );

    if (!whereMatches) {
      return false;
    }
  }

  // 3. Filtragem por termo de busca ("O que você procura?")
  if (searchWhat && searchWhat.trim()) {
    const queryTokens = normalizeSearchString(searchWhat).split(/\s+/).filter(Boolean);
    if (queryTokens.length === 0) return true;

    // Tokens de todos os campos da empresa
    const nameTokens = normalizeSearchString(business.name).split(/\s+/).filter(Boolean);
    const descTokens = normalizeSearchString(business.description).split(/\s+/).filter(Boolean);
    const kwTokens = normalizeSearchString(business.keywords).split(/\s+/).filter(Boolean);
    const cityTokens = normalizeSearchString(business.city).split(/\s+/).filter(Boolean);
    const addressTokens = normalizeSearchString(business.address).split(/\s+/).filter(Boolean);
    const slugTokens = normalizeSearchString(business.slug?.replace(/[-_]/g, ' ')).split(/\s+/).filter(Boolean);
    
    const catList = extractBusinessCategories(business);
    const catTokens = catList.flatMap(c => normalizeSearchString(c).split(/\s+/).filter(Boolean));

    // Se o termo buscado bate com o nome de uma categoria cadastrada no guia, adiciona tokens da categoria
    if (Array.isArray(guideCategories)) {
      for (const gc of guideCategories) {
        if (queryTokens.some(qTok => areTokensEquivalent(gc.name, qTok) || areTokensEquivalent(gc.slug, qTok))) {
          if (business.category_id && String(business.category_id) === String(gc.id)) {
            catTokens.push(...normalizeSearchString(gc.name).split(/\s+/).filter(Boolean));
          }
          if (business.guide_category_id && String(business.guide_category_id) === String(gc.id)) {
            catTokens.push(...normalizeSearchString(gc.name).split(/\s+/).filter(Boolean));
          }
        }
      }
    }

    const allBusinessTokens = [
      ...nameTokens,
      ...catTokens,
      ...kwTokens,
      ...descTokens,
      ...slugTokens,
      ...cityTokens,
      ...addressTokens
    ];

    // Cada token digitado pelo usuário na busca precisa encontrar pelo menos uma correspondência
    // inteligente (exata, stem, plural/singular ou aproximação) em algum campo da empresa
    const allTokensMatch = queryTokens.every(qTok => 
      allBusinessTokens.some(bTok => areTokensEquivalent(bTok, qTok))
    );

    if (!allTokensMatch) {
      return false;
    }
  }

  return true;
}
